import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { find } from '@/entities/coupon';
import { toBannerLinks } from '@/shared/lib/banner';
import { safeSrc } from '@/shared/lib/image';
import { isHttpUrl } from '@/shared/lib/url';
import { useAsync } from '@/shared/lib/use-async';
import { BannerList, BannerSlider } from '@/shared/ui/banner-slider/banner-slider';
import { Button } from '@/shared/ui/button/button';
import { Notice } from '@/shared/ui/notice/notice';
import { useToast } from '@/shared/ui/toast/toast';
import { LinkedText } from './linked-text';
import './coupon.css';

function PinLabel({ number }: { number: string }) {
  if (!isHttpUrl(number)) return <span>{number.match(/.{1,4}/g)?.join(' ') ?? number}</span>;

  return (
    <button type="button" className="coupon-box__pin-link" onClick={() => window.location.assign(number)}>
      {number}
    </button>
  );
}

interface LocationState {
  justExchanged?: boolean;
}

export interface CouponPageProps {
  token?: string | null;
}

export function CouponPage({ token }: CouponPageProps = {}) {
  const { seqnumber: paramSeqnumber = '' } = useParams();
  const seqnumber = token ?? paramSeqnumber;
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { data, loading, error, reload } = useAsync(() => find(seqnumber), [seqnumber]);

  const captureRef = useRef<HTMLDivElement>(null);
  const barcodeRef = useRef<HTMLCanvasElement>(null);
  const [saving, setSaving] = useState(false);
  const [corsFailedSrc, setCorsFailedSrc] = useState<string | null>(null);
  const [proxyFailed, setProxyFailed] = useState(false);
  const [copiedAt, setCopiedAt] = useState<number | null>(null);

  useEffect(() => {
    if (copiedAt === null) return;
    const timer = setTimeout(() => setCopiedAt(null), 1600);
    return () => clearTimeout(timer);
  }, [copiedAt]);

  const coupon = data && data.status === 'ok' ? data.coupon : null;

  useEffect(() => {
    const pin = coupon?.number;
    if (!pin) return;
    let cancelled = false;

    async function draw(value: string) {
      if (!barcodeRef.current) return;
      const { default: JsBarcode } = await import('jsbarcode');
      if (cancelled || !barcodeRef.current) return;
      const scale = window.devicePixelRatio || 1;
      JsBarcode(barcodeRef.current, value, {
        format: 'CODE128',
        width: 3 * scale,
        height: 80 * scale,
        margin: 10 * scale,
        displayValue: false,
        background: '#ffffff',
        lineColor: '#000000',
      });
    }

    draw(pin);
    return () => {
      cancelled = true;
    };
  }, [coupon]);

  useEffect(() => {
    if (!coupon) return;
    if (isHttpUrl(coupon.number)) window.location.assign(coupon.number);
  }, [coupon]);

  const justExchanged = (location.state as LocationState | null)?.justExchanged === true;
  // biome-ignore lint/correctness/useExhaustiveDependencies: 마운트 시점 1회만 확인하면 된다 — 재구독 불필요
  useEffect(() => {
    if (!justExchanged) return;
    toast.show({
      title: '쿠폰 교환이 완료되었어요!',
      description: '교환된 쿠폰은 기존 링크를 통해 확인할 수 있어요.',
    });
    navigate(location.pathname + location.search, { replace: true });
  }, []);

  async function handleCopy() {
    if (!coupon) return;
    try {
      await navigator.clipboard.writeText(coupon.number);
      setCopiedAt(Date.now());
    } catch {
      toast.show({ title: '복사에 실패했어요', description: '화면의 쿠폰번호를 보고 직접 입력해주세요.' });
    }
  }

  async function handleSave() {
    if (!coupon || saving || !captureRef.current) return;
    setSaving(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(captureRef.current, {
        useCORS: true,
        allowTaint: true,
        onclone: (doc) => {
          for (const el of doc.querySelectorAll<HTMLElement>('.coupon-box__copy, .coupon-box__copied')) {
            el.style.visibility = 'hidden';
          }
        },
      });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${coupon.name}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      toast.show({ title: '이미지 저장에 실패했어요', description: '잠시 후 다시 시도해주세요.' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <section className="coupon-state">
        <p role="status">쿠폰을 확인하는 중이에요…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="coupon-state">
        <p>쿠폰을 불러오지 못했어요 😥</p>
        <button type="button" className="coupon-state__retry" onClick={reload}>
          다시 시도
        </button>
      </section>
    );
  }

  if (data && (data.status === 'expired' || data.status === 'error')) return <Notice message={data.message} />;

  if (!coupon) return null;

  const proxy = proxyFailed ? null : coupon.imageProxy;
  const proxied = proxy !== null;
  const src = proxy ?? safeSrc(coupon.image);
  const topSlides = toBannerLinks(coupon.topBanners);
  const bottomSlides = toBannerLinks(coupon.bottomBanners);

  return (
    <>
      <section className="coupon-page">
        <div className="coupon-page__inner">
          <div className="capture" ref={captureRef}>
            <div className="box">
              <div className="coupon-box">
                <div className="coupon-box__content">
                  <figure className="coupon-box__img">
                    <img
                      src={src}
                      alt={`${coupon.brand} ${coupon.name}`}
                      crossOrigin={corsFailedSrc === src ? undefined : 'anonymous'}
                      onError={() => (proxied ? setProxyFailed(true) : setCorsFailedSrc(src))}
                    />
                  </figure>
                  <div className="coupon-box__detail">
                    <small className="coupon-box__brand">{coupon.brand}</small>
                    <span className="coupon-box__name">{coupon.name}</span>
                    <ul>
                      <li>
                        <span>교환처</span>
                        <p>{coupon.brand}</p>
                      </li>
                      <li>
                        <span>유효기간</span>
                        <p>{coupon.expired}</p>
                      </li>
                    </ul>
                    <figure className="coupon-box__barcode">
                      <canvas ref={barcodeRef} role="img" aria-label={`바코드 ${coupon.number}`} />
                      <div
                        className={
                          isHttpUrl(coupon.number)
                            ? 'coupon-box__number'
                            : 'coupon-box__number coupon-box__number--copyable'
                        }
                      >
                        <PinLabel number={coupon.number} />
                        {!isHttpUrl(coupon.number) ? (
                          <button
                            type="button"
                            className="coupon-box__copy"
                            aria-label="쿠폰번호 복사"
                            onClick={handleCopy}
                          >
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                              <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                              <path
                                d="M15 5H6a1 1 0 0 0-1 1v9"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        ) : null}
                        <span className="coupon-box__copied" role="status">
                          {copiedAt !== null ? '쿠폰번호를 복사했어요' : ''}
                        </span>
                      </div>
                    </figure>
                  </div>
                </div>
                <img className="coupon-box__logo" src="/images/coupon-logo.svg" alt="체인지콘 로고" />
              </div>
            </div>
          </div>

          <Button className="coupon-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중…' : '교환쿠폰 저장'}
          </Button>

          <div className="coupon-location">
            <h3>[교환쿠폰 저장] 클릭 시, 이미지 저장 경로</h3>
            <p>
              <b>- 안드로이드(AOS) :</b> '갤러리' 앱
              <br />
              <b>- 아이폰(IOS) :</b> '파일' 앱 &gt; '다운로드' 폴더
            </p>
          </div>

          <BannerSlider slides={topSlides} className="coupon-banner" />
        </div>
      </section>

      <section className="coupon-page-desc">
        <div className="coupon-page__inner">
          <ul className="coupon-desc">
            <li>
              <h3>사용가능매장</h3>
              {coupon.store.map((line) => (
                <LinkedText key={line} text={line} />
              ))}
            </li>
            <li>
              <h3>유의사항</h3>
              {coupon.precaution.map((line) => (
                <LinkedText key={line} text={line} />
              ))}
            </li>
            <li>
              <h3>제한사항</h3>
              {coupon.restriction.map((line) => (
                <LinkedText key={line} text={line} />
              ))}
            </li>
          </ul>

          <BannerList slides={bottomSlides} className="coupon-bottom-banner" />
        </div>
      </section>
    </>
  );
}
